/**
 * Dialog de confirmation lors d'une bascule de statut qui implique un SMS (iter43).
 *
 * Quand l'utilisateur veut passer un OR à `devis_envoye` ou `pret`, on lui propose 3 choix :
 *   - Annuler (ferme le dialog)
 *   - Continuer sans SMS (statut bascule + skip_sms=true)
 *   - Continuer avec SMS (statut bascule + skip_sms=false → SMS envoyé)
 *
 * La sémantique des statuts est préservée (devis_envoye = devis "officialisé",
 * pret = véhicule réceptionné par le client), mais l'admin peut choisir s'il
 * a déjà notifié le client par un autre canal (mail, téléphone direct…).
 */
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Send, SkipForward, X, AlertTriangle } from "lucide-react";

const COPY = {
  devis_envoye: {
    title: "Marquer le devis comme envoyé",
    icon: "📄",
    description:
      "Vous êtes sur le point de basculer ce dossier en « Devis envoyé ». " +
      "Un SMS avec le lien public de validation peut être envoyé automatiquement au client.",
    question: "Avez-vous déjà transmis le devis au client (mail, téléphone…) ?",
  },
  pret: {
    title: "Marquer le véhicule comme prêt",
    icon: "🚗",
    description:
      "Vous êtes sur le point de basculer ce dossier en « Prêt à récupérer ». " +
      "Un SMS de notification peut être envoyé automatiquement au client.",
    question: "Avez-vous déjà prévenu le client (téléphone, autre canal…) ?",
  },
};

export default function StatutSmsConfirmDialog({
  open,
  onClose,
  statutCible,         // 'devis_envoye' | 'pret'
  onContinueWithoutSms,
  onContinueWithSms,
  submitting = false,
}) {
  const copy = COPY[statutCible] || COPY.devis_envoye;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-lg" data-testid="statut-sms-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-xl">{copy.icon}</span>
            {copy.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-gray-700">{copy.description}</p>

          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2.5">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <strong>{copy.question}</strong>
              <p className="text-xs text-amber-800 mt-1">
                Choisissez « Sans SMS » pour éviter un doublon de notification au client.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            className="sm:mr-auto"
            data-testid="statut-sms-cancel"
          >
            <X className="w-4 h-4 mr-1.5" /> Annuler
          </Button>
          <Button
            variant="outline"
            onClick={onContinueWithoutSms}
            disabled={submitting}
            data-testid="statut-sms-skip"
          >
            <SkipForward className="w-4 h-4 mr-1.5" /> Continuer sans SMS
          </Button>
          <Button
            onClick={onContinueWithSms}
            disabled={submitting}
            className="bg-[#0891b2] hover:bg-[#0e7490]"
            data-testid="statut-sms-send"
          >
            <Send className="w-4 h-4 mr-1.5" /> Envoyer le SMS et continuer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
